import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Wrapper } from './style';
import { Link } from 'react-router-dom';
import { DatePicker, message, Select, Spin, Alert, Button } from 'antd';
import dayjs from 'dayjs';

// Config & API imports
import { GetMarketplaceDropdown, GetMarketplaceList, rangePresets } from '../../../config';
import { MakeApiCall } from '../../../apis';

// Child Components
import Tiles from './tiles';
import ChartComponent from './chart';
import PL from './pl';

const { Option } = Select;
const { RangePicker } = DatePicker;

const DEFAULT_PRODUCT_IMAGE = 'https://app.sellerview.ai/sellerview_api/images/no-product-image.png';
const AMAZON_DATE_FORMAT = 'YYYY-MM-DD';

const Dashboard = ({ userData }) => {
  // --- Connection Logic ---
  // Checking flags from userData.user.user_data as per your JSON structure
  const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
  const isAdsApiConnected = userData?.user?.user_data?.is_amz_ads_connected === 1;

  // --- State ---
  const [activeTab, setActiveTab] = useState('charts');
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [marketplaceList, setMarketplaceList] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [filterData, setFilterData] = useState({});
  const [selectedValue, setSelectedValue] = useState(null);
  const [customRange, setCustomRange] = useState([]);
  const [asinList, setAsinList] = useState([]);
  const [selectedAsins, setSelectedAsins] = useState([]);
  const [periodicity, setPeriodicity] = useState('day');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [otherRange, setOtherRange] = useState({
    start_date: dayjs().subtract(29, 'day').format(AMAZON_DATE_FORMAT),
    end_date: dayjs().format(AMAZON_DATE_FORMAT),
  });

  // --- Helpers & Memoized Values ---
  const dateFilterOptions = useMemo(() =>
      Object.entries(filterData).map(([key, group]) => ({
        label: Object.values(group).join(' / '),
        value: key
      })), [filterData]
  );

  const selectedSkus = useMemo(() =>
          selectedAsins.map(asin => asinList.find(i => i.asin === asin)?.sku).filter(Boolean),
      [selectedAsins, asinList]
  );

  // --- API Logic ---
  const fetchProductList = async (searchText = '') => {
    // Prevent API calls if SP-API is not connected
    if (!isSpApiConnected) return;

    setIsSearching(true);
    try {
      const url = `get-products?no_pagination=true${searchText ? `&search_text=${searchText}` : ''}`;
      const response = await MakeApiCall(url, 'get', null, true, {}, userData?.token);

      if (response?.status) {
        setAsinList((response?.data?.records || []).map(item => ({
          asin: item.asin,
          name: item.item_name,
          image: item.item_image,
          sku: item.sku,
        })));
      }
    } catch (error) {
      console.error('Product fetch error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useCallback(
      (() => {
        let timeout;
        return (val) => {
          setIsSearching(true);
          clearTimeout(timeout);
          timeout = setTimeout(() => fetchProductList(val), 500);
        };
      })(), [userData?.token, isSpApiConnected]
  );

  const loadInitialData = async () => {
    if (!isSpApiConnected) return; // Don't fetch dashboard data if not connected

    setLoading(true);
    try {
      const [mktResponse, filterResponse] = await Promise.all([
        GetMarketplaceDropdown(),
        MakeApiCall(`dashboard/tiles/get-filters-data`, "get", null, true, {}, userData?.token)
      ]);

      if (mktResponse?.marketplace_list?.length > 0) {
        setMarketplaceList(mktResponse.marketplace_list);
        const [mktId] = Object.entries(mktResponse.selected_marketplace || {})[0] || [];
        setSelectedMarketplace(mktResponse.marketplace_list.find(m => m.id === mktId));
      }

      if (filterResponse?.status) setFilterData(filterResponse.data || {});

      await fetchProductList();
    } catch (e) {
      message.error("Failed to sync dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  // --- Effects ---
  useEffect(() => {
    loadInitialData();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSpApiConnected]);

  useEffect(() => {
    if (Object.keys(filterData).length > 0 && !selectedValue) {
      setSelectedValue(Object.keys(filterData)[0]);
    }
  }, [filterData]);

  // --- Event Handlers ---
  const handleMobileDateChange = (date, type) => {
    if (!date) return;
    setOtherRange(prev => ({ ...prev, [type === 'start' ? 'start_date' : 'end_date']: date.format(AMAZON_DATE_FORMAT) }));
  };

  const handleRangeOther = (dates) => {
    const [start, end] = dates || [null, null];
    setOtherRange({
      start_date: (start || dayjs().subtract(29, 'day')).format(AMAZON_DATE_FORMAT),
      end_date: (end || dayjs()).format(AMAZON_DATE_FORMAT)
    });
  };

  // --- Render Helpers ---
  const renderTab = (key, label, iconClass) => (
      <li className="nav-item" key={key}>
        <Link
            className={`nav-link ${activeTab === key ? 'active' : ''}`}
            onClick={(e) => {
              if(!isSpApiConnected) e.preventDefault();
              setActiveTab(key);
            }}
        >
          <i className={`ki-outline ${iconClass} me-3 fs-2 text-gray-900`}></i>
          {label}
        </Link>
      </li>
  );

  return (
      <Wrapper>
        {/* 1. Conditional Banner Logic */}
        {!isSpApiConnected && (
            <Alert
                message={<span className="fw-bolder fs-5">Action Required: Connect Marketplace</span>}
                description={
                  <div>
                    <p className="mb-2">Your Amazon Selling Partner API is not connected. This is required to manage and sync your business expenses.</p>
                    <Link to="/settings/marketplace-credentials?tab=spApi&creds=true">
                      <Button type="primary" danger size="small">Connect SP-API</Button>
                    </Link>
                  </div>
                }
                type="error"
                showIcon
                className="mb-4 p-6"
            />
        )}

        {/* 2. Ads API Banner (Warning Level) */}
        {!isAdsApiConnected && (
            <Alert
                message={<span className="fw-bolder fs-6">Amazon Advertising Not Connected</span>}
                description={
                  <div>
                    <p className="mb-2">PPC and Advertising-related expenses will not be accurately reflected in your metrics.</p>
                    {/* Redirecting to advertising tab and triggering the auto-modal logic we discussed */}
                    <Link to="/settings/marketplace-credentials?tab=advertising&creds=true">
                      <Button type="primary" danger size="small">Connect Ads API</Button>
                    </Link>
                  </div>
                }
                type="error"
                showIcon
                closable={isSpApiConnected} // Only allow closing if the main API is connected
                className="mb-4 p-6"
            />
        )}

        {/* 2. Main Dashboard Content with Conditional Overlay */}
        <div style={{ position: 'relative' }}>

          {/* Overlay specifically for SP-API disconnection */}
          {!(isSpApiConnected && isAdsApiConnected) && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: -10,
                right: -10,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(5px)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px'
              }}>
                <div className="text-center bg-white p-8 shadow-sm rounded border">
                  <i className="ki-outline ki-lock-2 fs-3x text-muted mb-4"></i>
                  <h3 className="text-gray-800">Features Locked</h3>
                  <p className="text-muted">Connect your Amazon account to unlock analytics.</p>
                </div>
              </div>
          )}

          {/* Tabs Section */}
          <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
            <div className="position-relative d-flex wrap justify-content-between">
              <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                {renderTab('charts', 'Overview', 'ki-chart-pie-4')}
                {renderTab('pl', 'P&L Trends', 'ki-chart-line-up')}
              </ul>
            </div>
          </div>

          {/* Filters Section */}
          <div className="mb-5 d-flex flex-column flex-md-row gap-4 filters w-100 overflow-hidden px-1">
            {/* SKU Search */}
            <Select
                mode="multiple"
                showSearch
                placeholder="Search SKU, Title or ASIN"
                className="w-100 flex-md-grow-0"
                style={{ borderColor: 'black', width: isMobile ? '100%' : '350px' }}
                size="large"
                value={selectedAsins}
                onChange={setSelectedAsins}
                onSearch={(val) => { setIsSearching(true); debouncedSearch(val); }}
                filterOption={false}
                loading={isSearching && isAdsApiConnected && isSpApiConnected}
                optionLabelProp="value"
                maxTagCount="responsive"
                disabled={!isSpApiConnected}
            >
              {asinList.map(item => (
                  <Option key={item.asin} value={item.asin}>
                    <div className="d-flex align-items-center gap-3 py-1">
                      <img src={item.image || DEFAULT_PRODUCT_IMAGE} alt="" className="w-30px h-30px rounded border object-fit-contain" />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-bolder text-dark text-truncate" style={{ fontSize: '12px' }}>{item.name}</div>
                        <div className="fs-12 text-muted"><span className="badge badge-light-danger fw-bold">{item.asin}</span></div>
                      </div>
                    </div>
                  </Option>
              ))}
            </Select>

            {/* Dynamic Date Pickers Container */}
            <div className="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
              {activeTab === 'tiles' ? (
                  <Select
                      className="w-100 w-md-250px"
                      placeholder="Select Date Range"
                      onChange={(val) => { setSelectedValue(val); if (val !== 'custom') setCustomRange([]); }}
                      value={selectedValue}
                      size="large"
                      options={dateFilterOptions}
                      disabled={!isSpApiConnected}
                  />
              ) : (
                  isMobile ? (
                      <div className="d-flex gap-2 w-100">
                        <DatePicker disabled={!isSpApiConnected} value={dayjs(otherRange.start_date)} placeholder="Start" size="large" className="w-50" onChange={(d) => handleMobileDateChange(d, 'start')} />
                        <DatePicker disabled={!isSpApiConnected} value={dayjs(otherRange.end_date)} placeholder="End" size="large" className="w-50" onChange={(d) => handleMobileDateChange(d, 'end')} />
                      </div>
                  ) : (
                      <RangePicker
                          disabled={!isSpApiConnected}
                          size="large"
                          presets={rangePresets}
                          value={[dayjs(otherRange.start_date), dayjs(otherRange.end_date)]}
                          onChange={handleRangeOther}
                          className="w-md-250px"
                      />
                  )
              )}

              {activeTab !== 'tiles' && (
                  <Select
                      value={periodicity}
                      style={{ width: isMobile ? '100%' : '120px' }}
                      onChange={setPeriodicity}
                      size="large"
                      disabled={!isSpApiConnected}
                  >
                    <Option value="day">By Day</Option>
                    <Option value="month">By Month</Option>
                  </Select>
              )}
            </div>

            {/* Marketplace Select */}
            <Select
                className="w-100 w-md-200px"
                size="large"
                placeholder="Select Marketplace"
                options={GetMarketplaceList(marketplaceList)}
                value={selectedMarketplace?.id}
                onChange={(id) => setSelectedMarketplace(marketplaceList.find(m => m.id === id))}
                disabled={!isSpApiConnected}
            />
          </div>

          {/* Main Content Area */}
          <div className="w-100 overflow-hidden">
            {activeTab === 'tiles' && (
                <Tiles
                    customRange={customRange}
                    selectedValue={selectedValue}
                    userData={userData}
                    selectedMarketplace={selectedMarketplace}
                    selectedCurrencySign="₹"
                />
            )}
            {activeTab === 'charts' && (
                <ChartComponent
                    periodicity={periodicity}
                    otherRange={otherRange}
                    userData={userData}
                    selectedMarketplace={selectedMarketplace}
                    selectedCurrencySign="₹"
                    selectedSku={selectedSkus}
                />
            )}
            {activeTab === 'pl' && (
                <PL
                    periodicity={periodicity}
                    otherRange={otherRange}
                    userData={userData}
                    selectedMarketplace={selectedMarketplace}
                    selectedCurrencySign="₹"
                    selectedSku={selectedSkus}
                />
            )}
          </div>
        </div>
      </Wrapper>
  );
};

export default Dashboard;